import { knowledgebaseApiCalls } from "@/services/api/knowledgebaseService"
import type { KnowledgebaseFile } from "@/types/knowledgebase"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo } from "react"

const KNOWLEDGE_BASE_FILES_QUERY_KEY = ["files"] as const

const hasTaggedDocuments = (files: KnowledgebaseFile[]) =>
  files.some((file) => (file.tagIds?.length ?? 0) > 0)

export const useHasSelectableQuestionnaireDocuments = (enabled = true) => {
  const queryClient = useQueryClient()

  const cachedFiles = useMemo(() => {
    const cachedEntries = queryClient.getQueriesData<KnowledgebaseFile[]>({
      queryKey: KNOWLEDGE_BASE_FILES_QUERY_KEY,
    })

    return cachedEntries
      .map(([, data]) => data)
      .find((data): data is KnowledgebaseFile[] => Array.isArray(data))
  }, [queryClient])

  const shouldFetchFiles = enabled && cachedFiles === undefined

  const { data: fetchedFiles = [] } = useQuery({
    queryKey: [...KNOWLEDGE_BASE_FILES_QUERY_KEY, {}],
    queryFn: () => knowledgebaseApiCalls.getFiles(),
    enabled: shouldFetchFiles,
  })

  const hasSelectableQuestionnaireDocuments = useMemo(() => {
    if (!enabled) {
      return false
    }

    return hasTaggedDocuments(cachedFiles ?? fetchedFiles)
  }, [cachedFiles, enabled, fetchedFiles])

  return { hasSelectableQuestionnaireDocuments }
}
